'use client';

import React, { useEffect, useState } from 'react';
import { Container, Row } from 'reactstrap';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useLangContext } from '@/context/langContext';
import { useEnvContext } from '@/context/envContext';
import { pushGTMClickTags } from '@/libs/pushGTMTags/pushGTMTags';
const currentDate = new Date().getFullYear();
function getEnv(currentHost: string): 'eng' | 'qa' | 'prod' {
  if (currentHost?.includes('.eng')) return 'eng';
  if (currentHost?.includes('.qa')) return 'qa';
  return 'prod';
}
type SupportedLang = 'en' | 'de' | 'es' | 'fr' | 'pt' | 'ko' | 'zh' | 'jp';
function getGdprId(env: 'eng' | 'qa' | 'prod', lang: SupportedLang): string {
  const ids: Record<'eng' | 'qa' | 'prod', Record<SupportedLang, string>> = {
    eng: {
      en: '1035075',
      de: '1035063',
      es: '1035066',
      fr: '1035072',
      pt: '1035075',
      ko: '1035069',
      zh: '1035075',
      jp: '1284766'
    },
    qa: {
      en: '1027380',
      de: '1027365',
      es: '1027470',
      fr: '1027395',
      pt: '1027380',
      ko: '1028496',
      zh: '1027380',
      jp: '1330856'
    },
    prod: {
      en: '1048617',
      de: '1048533',
      es: '1048596',
      fr: '1048512',
      pt: '1048617',
      ko: '1048575',
      zh: '1048617',
      jp: '1337183'
    }
  };
  return ids[env][lang] || ids[env].en;
}
function getCcpaId(env: 'eng' | 'qa' | 'prod', lang: SupportedLang): string {
  const ids: Record<'eng' | 'qa' | 'prod', Record<SupportedLang, string>> = {
    eng: {
      en: '1048647',
      de: '1048653',
      es: '1048650',
      fr: '1048656',
      pt: '1048647',
      ko: '1048644',
      zh: '1048647',
      jp: '1297380'
    },
    qa: {
      en: '1027275',
      de: '1027245',
      es: '1027260',
      fr: '1027230',
      pt: '1027275',
      ko: '1027761',
      zh: '1027275',
      jp: '1330911'
    },
    prod: {
      en: '1048406',
      de: '1048469',
      es: '1048448',
      fr: '1048491',
      pt: '1048406',
      ko: '1048385',
      zh: '1048406',
      jp: '1337184'
    }
  };
  return ids[env][lang] || ids[env].en;
}

/**
 * Custom React hook to check and handle CCPA (California Consumer Privacy Act) compliance.
 * It uses the `__uspapi` function (if available) to retrieve user privacy settings.
 * If the response does not contain the string '---', it assumes the user should be shown a consent prompt.
 *
 * @param setShow - A React state setter function to control visibility of the CCPA consent prompt.
 * It's an updater function from "useState<boolean>()"
 * And can be called with a boolean value or a function that returns a boolean
 */
function useCCPA(setShow: React.Dispatch<React.SetStateAction<boolean>>) {
  useEffect(() => {
    let isMounted = true;
    if (typeof window !== 'undefined' && window.__uspapi) {
      window.__uspapi('getUSPData', 1, (uspData: any) => {
        if (!isMounted) return;
        const show = JSON.stringify(uspData).indexOf('---') === -1;
        setShow(show);
      });
    }
    return () => {
      isMounted = false;
    };
  }, [setShow]);
}

/**
 * Custom React hook to check and handle GDPR (General Data Protection Regulation) compliance.
 * It uses the `__tcfapi` function (if available) to retrieve GDPR applicability.
 * If GDPR applies, it sets the state to show the consent prompt.
 *
 * @param setShow - A React state setter function to control visibility of the GDPR consent prompt.
 * It's an updater function from `useState<boolean>()
 * And can be called with a boolean value or a function that returns a boolean
 */
function useGDPR(setShow: React.Dispatch<React.SetStateAction<boolean>>) {
  useEffect(() => {
    let isMounted = true;
    if (typeof window !== 'undefined' && window.__tcfapi) {
      window.__tcfapi('getTCData', 2, (data: any) => {
        if (!isMounted) return;
        setShow(!!data?.gdprApplies);
      });
    }
    return () => {
      isMounted = false;
    };
  }, [setShow]);
}
function Footer() {
  const [showGDPRCookies, setShowGDPRCookies] = useState(false);
  const [showCCPACookies, setShowCCPACookies] = useState(false);
  const {
    lang
  } = useLangContext();
  const [currentHost, setCurrentHost] = useState<string>('');
  const t = useTranslations('Footer');
  const {
    cdnEnv
  } = useEnvContext();
  const borderFlash = cdnEnv ? `${cdnEnv}/rms/images/border_diagonal.svg` : 'https://media.techtarget.com/rms/images/border_diagonal.svg';
  const desktopFallbackLogo = '/hub/images/TechTarget-Logo_color-black.svg';
  const desktopCdnLogo = cdnEnv ? `${cdnEnv}/rms/ux/responsive/img/techtarget_logo_black.svg` : 'https://media.techtarget.com/rms/ux/responsive/img/techtarget_logo_black.svg';
  const mobileFallbackLogo = '/hub/images/TechTarget-Logo_stacked_color-black.svg';
  const mobileCdnLogo = cdnEnv ? `${cdnEnv}/rms/ux/responsive/img/techtarget_logo_black_stacked.svg` : 'https://media.techtarget.com/rms/ux/responsive/img/techtarget_logo_black_stacked.svg';
  const [desktopLogoSrc, setDesktopLogoSrc] = useState(desktopCdnLogo);
  const [mobileLogoSrc, setMobileLogoSrc] = useState(mobileCdnLogo);
  useEffect(() => {
    setDesktopLogoSrc(desktopCdnLogo);
  }, [desktopCdnLogo]);
  useEffect(() => {
    setMobileLogoSrc(mobileCdnLogo);
  }, [mobileCdnLogo]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCurrentHost(window.location.host);
    }
  }, []);
  useCCPA(setShowCCPACookies);
  useGDPR(setShowGDPRCookies);
  const env = getEnv(currentHost);
  return <div className={'site-footer'} data-sentry-component="Footer" data-sentry-source-file="Footer.tsx">
   <Container className={'footer-main g-0 xxl'} data-sentry-element="Container" data-sentry-source-file="Footer.tsx">
    <Row data-sentry-element="Row" data-sentry-source-file="Footer.tsx">
     <div className={'footer-logo-flex'}>
      <Image className="d-none d-md-block" src={desktopLogoSrc} width={'186'} height={'32'} style={{
            width: 'auto',
            height: '32px'
          }} priority={true} alt={'Techtarget logo'} onError={() => {
            setDesktopLogoSrc(desktopFallbackLogo);
          }} data-sentry-element="Image" data-sentry-source-file="Footer.tsx" />
      <Image className="d-block d-md-none" src={mobileLogoSrc} width={'84'} height={'44'} style={{
            width: 'auto',
            height: '44px'
          }} priority={true} alt={'Techtarget logo stacked'} onError={() => {
            setMobileLogoSrc(mobileFallbackLogo);
          }} data-sentry-element="Image" data-sentry-source-file="Footer.tsx" />
     </div>
     <div className={'footer-main-flex'}>
      <div aria-hidden="true" style={{
            backgroundImage: `url(${borderFlash})`,
            backgroundRepeat: 'repeat-x',
            width: '100%',
            height: '10px'
          }}></div>
      <div className={'foot-meta'}>
       <p className={'foot-copyright'}>
        {t('Copyright', {
                year: currentDate
              })}
       </p>
       <div className={'footer-links'}>
        <a className={'text-links-sm privacy'} target="_blank" href={'https://www.informatechtarget.com/privacy-policy/'} onClick={() => {
                pushGTMClickTags({
                  click_element: 'Anchor',
                  click_section_ID: 'NONE',
                  link_classes: 'text-links-sm privacy',
                  link_id: 'NONE',
                  link_text: ' Privacy policy',
                  link_url: 'https://www.informatechtarget.com/privacy-policy/',
                  click_category: 'NONE',
                  click_destination: 'Privacy policy',
                  click_closest_href: 'NONE'
                });
              }}>
         {t('PrivacyPolicy')}
        </a>

        <a className={'text-links-sm privacy'} target="_blank" href={'https://techtarget.zendesk.com/hc/en-us/requests/new?ticket_form_id=360004852434'} onClick={() => {
                pushGTMClickTags({
                  click_element: 'Anchor',
                  click_section_ID: 'NONE',
                  link_classes: 'text-links-sm privacy',
                  link_id: 'NONE',
                  link_text: ' Do Not Sell or Share My Personal Information',
                  link_url: 'https://techtarget.zendesk.com/hc/en-us/requests/new?ticket_form_id=360004852434',
                  click_category: 'NONE',
                  click_destination: 'ZenDesk ticket request',
                  click_closest_href: 'NONE'
                });
              }}>
         Do Not Sell or Share My Personal Information
        </a>
        <button id="gdpr_link" className={'text-links-sm'} style={{
                display: showGDPRCookies ? '' : 'none'
              }} onClick={() => {
                window?._sp_?.gdpr.loadPrivacyManagerModal(getGdprId(env, lang));
                pushGTMClickTags({
                  click_element: 'Button',
                  click_section_ID: 'gdpr_link',
                  link_classes: 'text-links-sm',
                  link_id: 'gdpr_link',
                  link_text: 'Cookie Preferences',
                  link_url: 'NONE',
                  click_category: 'NONE',
                  click_destination: 'gdpr popup',
                  click_closest_href: 'NONE'
                });
              }} data-bdd={getGdprId(env, lang)}>
         {t('CookiePreferences')}
        </button>
        <button id="ccpa_link" className={'text-links-sm'} style={{
                display: showCCPACookies ? '' : 'none'
              }} onClick={() => {
                window._sp_?.usnat.loadPrivacyManagerModal(getCcpaId(env, lang));
                pushGTMClickTags({
                  click_element: 'Button',
                  click_closest_class: 'NONE',
                  click_section_ID: 'ccpa_link',
                  link_classes: 'text-links-sm',
                  link_id: 'ccpa_link',
                  link_text: 'Cookie Preferences',
                  link_url: 'NONE',
                  click_category: 'NONE',
                  click_destination: 'ccpa popup',
                  click_closest_href: 'NONE'
                });
              }} data-bdd={getCcpaId(env, lang)}>
         {t('CookiePreferences')}
        </button>
       </div>
      </div>
     </div>
    </Row>
   </Container>
  </div>;
}
export default Footer;